import React, { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { supabase } from '../../services/supabase'

interface W9FormSignNowProps {
  talentId: string
  onComplete: () => void
}

type Step = 'loading' | 'send' | 'sent' | 'completed' | 'error'

const W9FormSignNow: React.FC<W9FormSignNowProps> = ({ talentId, onComplete }) => {
  const [step, setStep] = useState<Step>('loading')
  const [userEmail, setUserEmail] = useState<string>('')
  const [signerEmail, setSignerEmail] = useState<string>('')
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [differentEmail, setDifferentEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    init()
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const init = async () => {
    try {
      // Load the user's email
      const { data: authData } = await supabase.auth.getUser()
      const email = authData?.user?.email || ''
      setUserEmail(email)
      setSignerEmail(email)

      // Check for an existing envelope
      const { data: existing } = await supabase
        .from('w9_envelopes')
        .select('status, signer_email')
        .eq('talent_id', talentId)
        .maybeSingle()

      if (existing?.status === 'completed') {
        setStep('completed')
        onComplete()
        return
      }

      if (existing?.status === 'invited') {
        // Already sent — go straight to the waiting screen
        if (existing.signer_email) setSignerEmail(existing.signer_email)
        setStep('sent')
        startPolling()
        return
      }

      setStep('send')
    } catch (err) {
      console.error('Error initialising W-9 step:', err)
      setStep('send')
    }
  }

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await supabase
          .from('w9_envelopes')
          .select('status')
          .eq('talent_id', talentId)
          .maybeSingle()

        if (data?.status === 'completed') {
          if (pollRef.current) clearInterval(pollRef.current)
          setStep('completed')
          toast.success('W-9 signed — moving to the next step!')
          onComplete()
        }
      } catch {
        // silent — keep polling
      }
    }, 5000)
  }

  const sendInvite = async (emailOverride?: string) => {
    const targetEmail = emailOverride || signerEmail
    if (!targetEmail) {
      toast.error('Please enter an email address.')
      return
    }

    setSending(true)
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'signnow-create-w9-invite',
        { body: { talentId, signerEmail: targetEmail } }
      )

      if (invokeError) throw new Error(invokeError.message)
      if (data?.error) throw new Error(data.error)

      setSignerEmail(targetEmail)
      setShowEmailInput(false)
      setDifferentEmail('')
      setStep('sent')
      startPolling()
      toast.success(`W-9 sent to ${targetEmail}`)
    } catch (err: any) {
      console.error('Error sending W-9 invite:', err)
      setErrorMsg(err.message || 'Failed to send signing invite.')
      toast.error('Failed to send W-9 — please try again.')
    } finally {
      setSending(false)
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (step === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4" />
        <p className="text-white text-lg">Loading W-9 form…</p>
      </div>
    )
  }

  // ── Send invite ───────────────────────────────────────────────────────────
  if (step === 'send') {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-purple-500/50 rounded-lg p-6">
          <h3 className="text-white font-semibold text-lg mb-2">Sign Your W-9 Form</h3>
          <p className="text-gray-300 text-sm mb-3">
            We'll send you an email with a secure link to sign your W-9 electronically. You can sign from any
            device — no special app required.
          </p>
          <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
            <li>Check your inbox for the signing link</li>
            <li>Fill in all required fields and sign</li>
            <li>Once signed, this step completes automatically</li>
          </ul>
        </div>

        <div className="flex flex-col items-center gap-4">
          {errorMsg && (
            <div className="w-full max-w-md bg-red-500/20 border border-red-500 rounded-lg px-4 py-3 text-red-300 text-sm">
              {errorMsg}
            </div>
          )}

          {/* Primary send button */}
          {!showEmailInput && (
            <button
              onClick={() => sendInvite()}
              disabled={sending}
              className="w-full max-w-md px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
            >
              {sending ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending…
                </span>
              ) : (
                `Send W-9 to ${signerEmail || 'my email'}`
              )}
            </button>
          )}

          {/* Different email input */}
          {showEmailInput ? (
            <div className="w-full max-w-md space-y-3">
              <label className="block text-sm text-gray-400">Enter a different email address</label>
              <input
                type="email"
                value={differentEmail}
                onChange={(e) => setDifferentEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => sendInvite(differentEmail)}
                  disabled={sending || !differentEmail.trim()}
                  className="flex-1 px-6 py-3 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
                >
                  {sending ? 'Sending…' : 'Send to this email'}
                </button>
                <button
                  onClick={() => { setShowEmailInput(false); setDifferentEmail('') }}
                  className="px-4 py-3 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowEmailInput(true)}
              className="text-sm text-blue-400 hover:text-blue-300 underline transition-colors"
            >
              Send to a different email
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── Waiting for signature ─────────────────────────────────────────────────
  if (step === 'sent') {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-10 text-center">
          {/* Envelope icon */}
          <div className="mb-6 w-20 h-20 rounded-full flex items-center justify-center bg-blue-500/20">
            <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h3 className="text-2xl font-bold text-white mb-2">Check Your Email</h3>
          <p className="text-gray-300 max-w-sm mb-1">
            We sent your W-9 signing link to
          </p>
          <p className="text-blue-300 font-semibold text-lg mb-6 break-all">{signerEmail}</p>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-5 py-4 max-w-sm mb-8 text-left">
            <p className="text-yellow-200 text-sm font-medium mb-1">This step won't advance until you sign.</p>
            <p className="text-yellow-200/70 text-xs">
              Open the email, follow the link, and complete your signature. This page updates automatically once
              SignNow confirms your signature.
            </p>
          </div>

          {/* Pulse indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
            </span>
            Waiting for your signature…
          </div>

          {/* Resend / different email */}
          {!showEmailInput ? (
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => sendInvite()}
                disabled={sending}
                className="px-6 py-2.5 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm disabled:opacity-50"
              >
                {sending ? 'Resending…' : 'Resend email'}
              </button>
              <button
                onClick={() => setShowEmailInput(true)}
                className="text-sm text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Send to a different email
              </button>
            </div>
          ) : (
            <div className="w-full max-w-xs space-y-3">
              <input
                type="email"
                value={differentEmail}
                onChange={(e) => setDifferentEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => sendInvite(differentEmail)}
                  disabled={sending || !differentEmail.trim()}
                  className="flex-1 px-4 py-2.5 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
                <button
                  onClick={() => { setShowEmailInput(false); setDifferentEmail('') }}
                  className="px-4 py-2.5 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Completed (shouldn't be visible long — onComplete fires) ─────────────
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-white mb-2">W-9 Signed!</h3>
      <p className="text-gray-400 text-sm">Moving to the next step…</p>
    </div>
  )
}

export default W9FormSignNow
